import { createContext, useCallback, useMemo, useState } from "react";
import {
  GET_BUYER_ENDPOINT,
  GET_SEWING_LINE_ENDPOINT,
  STORE_REQUISITION_BOM_DETAILS,
  STORE_REQUISITION_BUYER_WISE_DETAILS,
  SUB_CONTRACT_FACTORY,
} from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";

export const StoreRequisitionContext = createContext(null);

const StoreRequisitionContextProvider = ({ children }) => {
  const [buyerList, setBuyerList] = useState([]);
  const [factoryList, setFactoryList] = useState([]);
  const [sewingLineList, setSewingLineList] = useState([]);
  const [styleList, setStyleList] = useState([]);
  const [bomDetails, setBomDetails] = useState([]);
  const [styleDetails, setStyleDetails] = useState([]);

  const getBuyerList = useCallback(async (filterValues = {}) => {
    const response = await getData(GET_BUYER_ENDPOINT, false, {
      page: 1,
      per_page: 500,
      ...filterValues,
    });
    if (response?.data?.code === 200 || response?.status === 200) {
      setBuyerList(response?.data?.data?.data || []);
    }
  }, []);

  const getFactoryList = useCallback(async (filterValues = {}) => {
    const response = await getData(SUB_CONTRACT_FACTORY, false, {
      page: 1,
      per_page: 500,
      ...filterValues,
    });
    if (response?.data?.code === 200 || response?.status === 200) {
      setFactoryList(response?.data?.data?.data || []);
    }
  }, []);

  const getSewingLineList = useCallback(async () => {
    const response = await getData(GET_SEWING_LINE_ENDPOINT, false, {
      page: 1,
      per_page: 500,
    });
    if (response?.data?.code === 200 || response?.status === 200) {
      const data = response?.data?.data;
      const list = Array.isArray(data) ? data : data?.data || [];
      setSewingLineList(list);
    }
  }, []);

  const getStyleListByBuyerOrFactory = useCallback(
    async ({ buyer_id = null, sub_contract_factory_id = null } = {}) => {
      if (!buyer_id && !sub_contract_factory_id) {
        setStyleList([]);
        return [];
      }

      const response = await getData(
        STORE_REQUISITION_BUYER_WISE_DETAILS,
        false,
        {
          buyer_id,
          sub_contract_factory_id,
          per_page: 1000,
        },
      );
      if (response?.data?.code === 200) {
        const data = response?.data?.data;
        const styles = Array.isArray(data) ? data : data?.data || [];
        setStyleList(styles);
        return styles;
      }
      setStyleList([]);
      return [];
    },
    [],
  );

  const getBomDetailsByStyleId = useCallback(async (styleId, options = {}) => {
    const { sr_type, item_category_ids = [] } = options;
    const params = {
      style_id: styleId,
      sr_type,
    };

    if (Array.isArray(item_category_ids) && item_category_ids.length > 0) {
      params.item_category_ids = item_category_ids;
    }

    const response = await getData(STORE_REQUISITION_BOM_DETAILS, false, params);
    if (response?.data?.code === 200) {
      const details = response?.data?.data || [];
      setBomDetails(details);
      return details;
    }
    setBomDetails([]);
    return [];
  }, []);

  const addStyleDetail = useCallback((styleRow) => {
    const key =
      styleRow.key || `style-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const newRow = { ...styleRow, key, items: styleRow.items || [] };
    setStyleDetails((prev) => [...prev, newRow]);
    return newRow;
  }, []);

  const updateStyleDetail = useCallback((key, updates) => {
    setStyleDetails((prev) =>
      prev.map((row) =>
        row.key === key
          ? {
              ...row,
              ...updates,
              items: updates.items ?? row.items ?? [],
            }
          : row,
      ),
    );
  }, []);

  const removeStyleDetail = useCallback((key) => {
    setStyleDetails((prev) => prev.filter((row) => row.key !== key));
  }, []);

  const addItemToStyle = useCallback((styleKey, itemRow) => {
    const itemKey =
      itemRow.key || `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setStyleDetails((prev) =>
      prev.map((row) => {
        if (row.key !== styleKey) return row;
        return {
          ...row,
          items: [...(row.items || []), { ...itemRow, key: itemKey }],
        };
      }),
    );
  }, []);

  const updateItemInStyle = useCallback((styleKey, itemKey, updates) => {
    setStyleDetails((prev) =>
      prev.map((row) => {
        if (row.key !== styleKey) return row;
        return {
          ...row,
          items: (row.items || []).map((item) =>
            item.key === itemKey ? { ...item, ...updates } : item,
          ),
        };
      }),
    );
  }, []);

  const removeItemFromStyle = useCallback((styleKey, itemKey) => {
    setStyleDetails((prev) =>
      prev.map((row) => {
        if (row.key !== styleKey) return row;
        return {
          ...row,
          items: (row.items || []).filter((item) => item.key !== itemKey),
        };
      }),
    );
  }, []);

  const resetStyleDetails = useCallback(() => {
    setStyleDetails([]);
    setBomDetails([]);
  }, []);

  const loadStyleDetailsFromApi = useCallback(
    (apiStyleDetails = [], stylesFromApi = []) => {
      const mapped = apiStyleDetails.map((style, index) => {
        const matchedStyle = stylesFromApi.find((s) => s.id === style.style_id);
        return {
          key: `style-${style.id || index}`,
          id: style.id,
          style_id: style.style_id,
          style_no: style.style?.style_no,
          sales_contract_id: style.sales_contract_id,
          sales_contract_no: style.sales_contract?.reference_no,
          buying_office_id: style.buying_office_id,
          buying_office_name: style.buying_office?.name,
          po_map_id: style.po_map_id,
          original_po: style.po_map?.original_po,
          sub_style_map: matchedStyle?.sub_style_map || [],
          items: (style.style_wise_sr_items || []).map((item, itemIndex) => ({
            key: `item-${item.id || itemIndex}`,
            id: item.id,
            item_category_id: item.item_category_id,
            item_category_name: item.item_category?.name,
            item_id: item.item_id,
            item_name: item.item?.name,
            sub_style_id: item.sub_style_id,
            sub_style_no: item.sub_style?.sub_style_no,
            color_id: item.color_id,
            color_name: item.color?.name,
            unit_id: item.unit_id,
            unit_name: item.unit?.name,
            order_qty: item.order_qty,
            yy: item.yy ?? item.per_product_required_quantity ?? null,
            convertion_rate: item.convertion_rate,
            roll_qty: item.roll_qty,
            convertion_unit_id: item.convertion_unit_id,
            convertion_unit_name: item.convertion_unit?.name,
            qty: item.qty,
          })),
        };
      });
      setStyleDetails(mapped);
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      buyerList,
      factoryList,
      sewingLineList,
      styleList,
      bomDetails,
      styleDetails,
      getBuyerList,
      getFactoryList,
      getSewingLineList,
      getStyleListByBuyerOrFactory,
      getBomDetailsByStyleId,
      addStyleDetail,
      updateStyleDetail,
      removeStyleDetail,
      addItemToStyle,
      updateItemInStyle,
      removeItemFromStyle,
      resetStyleDetails,
      loadStyleDetailsFromApi,
      setStyleDetails,
    }),
    [
      buyerList,
      factoryList,
      sewingLineList,
      styleList,
      bomDetails,
      styleDetails,
      getBuyerList,
      getFactoryList,
      getSewingLineList,
      getStyleListByBuyerOrFactory,
      getBomDetailsByStyleId,
      addStyleDetail,
      updateStyleDetail,
      removeStyleDetail,
      addItemToStyle,
      updateItemInStyle,
      removeItemFromStyle,
      resetStyleDetails,
      loadStyleDetailsFromApi,
    ],
  );

  return (
    <StoreRequisitionContext.Provider value={contextValue}>
      {children}
    </StoreRequisitionContext.Provider>
  );
};

export default StoreRequisitionContextProvider;
